"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock4,
  MapPin,
  ShieldCheck,
  Users,
  Zap,
} from "lucide-react"

// ─── Types ─────────────────────────────────────────────────────────────────

type Sector = {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  roles: string[]
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BRAND_CLIENTS = [
  "Caterpillar",
  "Amazon Logistics",
  "XPO Logistics",
  "Cardinal Health",
  "McKesson",
  "Walgreens",
  "Grainger",
  "Cardinal Glass",
  "DSC Logistics",
  "Shein US",
  "CEVA Logistics",
  "Kroger DFW",
]

const SECTORS: Sector[] = [
  {
    id: "forklift",
    label: "Forklift & Heavy Equipment Operators",
    icon: Zap,
    roles: ["Stand-Up / Sit-Down Forklift", "Reach Truck", "Cherry Picker / Order Picker", "Heavy Equipment Loader"],
  },
  {
    id: "assembly",
    label: "Assembly & Production Technicians",
    icon: Users,
    roles: ["Production Assembly", "Light Mechanical Assembly", "Electronics Assembly", "Quality Control Inspector"],
  },
  {
    id: "logistics",
    label: "Logistics & Supply Chain",
    icon: MapPin,
    roles: ["Warehouse Associate", "Receiving / Shipping Clerk", "Inventory Specialist", "Load Planner"],
  },
  {
    id: "maintenance",
    label: "Industrial Maintenance & janitorial",
    icon: ShieldCheck,
    roles: ["Industrial Maintenance Tech", "Facilities Janitorial", "Machine Operator", "Tool & Die"],
  },
]

// ─── Micro Components ───────────────────────────────────────────────────────

function MicroLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-mono text-[0.65rem] font-black uppercase tracking-[0.28em] text-primary">
      {children}
    </p>
  )
}

function LogoMark() {
  return (
    <Link href="/" className="group flex items-center gap-3" aria-label="Blue Core Staffing — Home">
      <div className="relative h-12 w-28 shrink-0">
        <Image
          src="/brand/logo.png"
          alt="Blue Core Staffing logo"
          fill
          sizes="112px"
          className="object-contain"
          priority
        />
      </div>
    </Link>
  )
}

// ─── Sticky Navigation ───────────────────────────────────────────────────────

function StickyHeader() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24)
    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const navLinks = [
    ["Our Process", "#call-off"],
    ["Industries", "#sectors"],
    ["Why Us", "#edge"],
    ["Contact", "#footer-form"],
  ]

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color,backdrop-filter] duration-700 ease-out ${
        scrolled
          ? "border-b border-border/70 bg-white shadow-theme-card backdrop-blur-xl"
          : "border-b border-transparent bg-background/0 backdrop-blur-0"
      }`}
    >
      <div className="mx-auto flex w-full max-w-[94rem] items-center justify-between gap-4 px-6 py-4 sm:px-10">
        <LogoMark />
        {/* Desktop nav */}
        <nav aria-label="Primary navigation" className="hidden lg:flex items-center gap-2 sm:gap-3">
          {navLinks.map(([label, href]) => (
            <Link
              key={label}
              href={href}
              onClick={() => setMenuOpen(false)}
              className={`rounded-full border px-4 py-2 text-center font-mono text-[0.62rem] font-black uppercase tracking-[0.18em] backdrop-blur transition-colors duration-500 ${
                scrolled
                  ? "border-border bg-white text-primary hover:border-primary/50 hover:text-primary"
                  : "border-white/18 bg-white/[0.06] text-white/70 hover:border-primary/50 hover:text-primary"
              }`}
            >
              {label}
            </Link>
          ))}
          <MagneticButton href="#footer-form" variant="primary">
            Get Started
            <ArrowRight className="size-4" />
          </MagneticButton>
        </nav>
        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
          className={`lg:hidden relative z-50 flex size-10 items-center justify-center rounded-full border-2 transition-colors ${
            scrolled
              ? "border-foreground/30"
              : "border-white text-white"
          }`}
        >
          <div className="flex flex-col gap-1.5">
            <span className={`block h-[2px] w-5 rounded-full transition-transform duration-300 ${
              scrolled ? "bg-foreground" : "bg-white"
            } ${menuOpen ? "translate-y-[4px] rotate-45" : ""}`} />
            <span className={`block h-[2px] w-5 rounded-full transition-all duration-300 ${
              scrolled ? "bg-foreground" : "bg-white"
            } ${menuOpen ? "opacity-0" : ""}`} />
            <span className={`block h-[2px] w-5 rounded-full transition-transform duration-300 ${
              scrolled ? "bg-foreground" : "bg-white"
            } ${menuOpen ? "-translate-y-[4px] -rotate-45" : ""}`} />
          </div>
        </button>
      </div>
      {/* Mobile menu dropdown */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        } ${scrolled ? "bg-white" : "bg-background/90 backdrop-blur-xl"}`}
      >
        <nav className="flex flex-col gap-2 px-6 pb-6 pt-2">
          {navLinks.map(([label, href]) => (
            <Link
              key={label}
              href={href}
              onClick={() => setMenuOpen(false)}
              className={`rounded-full border px-4 py-3 text-center font-mono text-[0.62rem] font-black uppercase tracking-[0.18em] transition-colors ${
                scrolled
                  ? "border-border bg-white text-primary hover:border-primary/50 hover:text-primary"
                  : "border-white/18 bg-white/[0.06] text-white/70 hover:border-primary/50 hover:text-primary"
              }`}
            >
              {label}
            </Link>
          ))}
          <Link
            href="#footer-form"
            onClick={() => setMenuOpen(false)}
            className="mt-2 inline-flex items-center justify-center gap-3 rounded-button bg-primary px-7 py-4 font-heading text-sm font-extrabold uppercase tracking-[0.14em] text-primary-foreground border-2 border-primary hover:shadow-theme-glow"
          >
            Get Started
            <ArrowRight className="size-4" />
          </Link>
        </nav>
      </div>
    </header>
  )
}

// ─── Particle Canvas (lightweight, no library) ───────────────────────────────

function ParticleCanvas({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    let animId: number
    let mouseX = 0.5
    let mouseY = 0.5

    const resize = () => {
      if (!canvas) return
      canvas.width = canvas.offsetWidth * window.devicePixelRatio
      canvas.height = canvas.offsetHeight * window.devicePixelRatio
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
    }

    interface Particle {
      x: number
      y: number
      vx: number
      vy: number
      size: number
      opacity: number
      phase: number
    }

    const particles: Particle[] = []
    const COUNT = 60

    const init = () => {
      particles.length = 0
      for (let i = 0; i < COUNT; i++) {
        particles.push({
          x: Math.random() * canvas.offsetWidth,
          y: Math.random() * canvas.offsetHeight,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          size: Math.random() * 2.5 + 0.8,
          opacity: Math.random() * 0.45 + 0.1,
          phase: Math.random() * Math.PI * 2,
        })
      }
    }

    const draw = () => {
      if (!canvas) return
      const w = canvas.offsetWidth
      const h = canvas.offsetHeight
      ctx.clearRect(0, 0, w, h)

      // Mouse influence
      const mx = mouseX * w
      const my = mouseY * h

      for (const p of particles) {
        const dx = mx - p.x
        const dy = my - p.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        const maxDist = 200

        if (dist < maxDist) {
          const force = (1 - dist / maxDist) * 0.4
          p.vx += dx * force * 0.008
          p.vy += dy * force * 0.008
        }

        p.x += p.vx
        p.y += p.vy
        p.vx *= 0.97
        p.vy *= 0.97

        if (p.x < 0) { p.x = w; p.vx *= -0.5 }
        if (p.x > w) { p.x = 0; p.vx *= -0.5 }
        if (p.y < 0) { p.y = h; p.vy *= -0.5 }
        if (p.y > h) { p.y = 0; p.vy *= -0.5 }

        p.phase += 0.008
        const pulse = (Math.sin(p.phase) + 1) / 2

        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(54,118,142,${p.opacity * (0.6 + pulse * 0.4)})`
        ctx.fill()

        // Connect nearby particles with faint lines
        for (const q of particles) {
          const dqx = q.x - p.x
          const dqy = q.y - p.y
          const dq = Math.sqrt(dqx * dqx + dqy * dqy)
          if (dq < 100) {
            ctx.beginPath()
            ctx.moveTo(p.x, p.y)
            ctx.lineTo(q.x, q.y)
            ctx.strokeStyle = `rgba(54,118,142,${(1 - dq / 100) * 0.18})`
            ctx.lineWidth = 0.5
            ctx.stroke()
          }
        }
      }

      animId = requestAnimationFrame(draw)
    }

    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX / (canvas?.offsetWidth ?? 1)
      mouseY = e.clientY / (canvas?.offsetHeight ?? 1)
    }

    const onResize = () => { resize(); init() }

    resize()
    init()
    draw()

    window.addEventListener("mousemove", onMove, { passive: true })
    window.addEventListener("resize", onResize)

    return () => {
      cancelAnimationFrame(animId)
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("resize", onResize)
    }
  }, [])

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />
}

// ─── Magnetic Button ────────────────────────────────────────────────────────

function MagneticButton({
  href,
  variant = "primary",
  children,
}: {
  href: string
  variant?: "primary" | "ghost" | "dark"
  children: React.ReactNode
}) {
  const btnRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const el = btnRef.current
    if (!el) return

    const handleMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      const dx = (e.clientX - cx) / (rect.width / 2)
      const dy = (e.clientY - cy) / (rect.height / 2)
      el.style.transform = `translate(${dx * 8}px, ${dy * -5}px)`
    }

    const handleMouseLeave = () => {
      el.style.transform = ""
    }

    el.addEventListener("mousemove", handleMouseMove as EventListener)
    el.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      el.removeEventListener("mousemove", handleMouseMove as EventListener)
      el.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [])

  return (
    <a
      ref={btnRef}
      href={href}
      className={`
        inline-flex items-center justify-center gap-3 rounded-button px-7 py-4 font-heading text-sm font-extrabold uppercase tracking-[0.14em]
        transition-[transform,box-shadow,border-color,background] duration-300 ease-out
        ${variant === "primary"
          ? "bg-primary text-primary-foreground border-2 border-primary hover:border-primary/80 hover:shadow-theme-glow"
          : variant === "dark"
            ? "bg-foreground text-background border-2 border-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-theme-glow"
            : "border-2 border-white/40 bg-transparent text-white backdrop-blur hover:border-white/70 hover:bg-white/8 hover:shadow-theme-glow"
        }
      `}
      style={{ transitionProperty: "transform, box-shadow, border-color, background" }}
    >
      {children}
    </a>
  )
}

// ─── Odometer Counter ───────────────────────────────────────────────────────

function OdometerCounter({
  target,
  suffix = "",
  prefix = "",
  duration = 2000,
}: {
  target: number
  suffix?: string
  prefix?: string
  duration?: number
}) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const started = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true
          const start = Date.now()
          const tick = () => {
            const elapsed = Date.now() - start
            const progress = Math.min(elapsed / duration, 1)
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3)
            setCount(Math.round(eased * target))
            if (progress < 1) requestAnimationFrame(tick)
          }
          requestAnimationFrame(tick)
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [target, duration])

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  )
}

// ─── Marquee ────────────────────────────────────────────────────────────────

function Marquee({ items, speed = 30 }: { items: string[]; speed?: number }) {
  const doubled = [...items, ...items]

  return (
    <div className="relative overflow-hidden">
      <div
        className="flex gap-12 whitespace-nowrap"
        style={{
          animation: `marquee ${items.length * speed}s linear infinite`,
        }}
      >
        {doubled.map((item, i) => (
          <span
            key={i}
            className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-white/50"
          >Contact Blue Core Staffing</span>
        ))}
      </div>
    </div>
  )
}

// ─── Sector Accordion ────────────────────────────────────────────────────────

function SectorAccordion({ sector, open, onToggle }: { sector: Sector; open: boolean; onToggle: () => void }) {
  const Icon = sector.icon

  return (
    <article className="border border-border rounded-panel overflow-hidden transition-all duration-300">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-7 py-5 text-left transition-colors hover:bg-muted/50"
        aria-expanded={open}
      >
        <div className="flex items-center gap-4">
          <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
            <Icon className="size-4 text-primary" />
          </div>
          <span className="font-heading text-base font-bold leading-tight">{sector.label}</span>
        </div>
        <div className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}>
          <ChevronDown className="size-5 text-muted-foreground" />
        </div>
      </button>

      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          open ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        }`}
        style={{ transitionDuration: open ? "350ms" : "200ms" }}
      >
        <div className="px-7 pb-6">
          <div className="mb-5 grid gap-2 sm:grid-cols-2">
            {sector.roles.map((role) => (
              <div key={role} className="flex items-center gap-2">
                <CheckCircle2 className="size-4 shrink-0 text-primary" />
                <span className="text-sm text-muted-foreground">{role}</span>
              </div>
            ))}
          </div>
          <Link
            href="#footer-form"
            className="inline-flex items-center gap-2 rounded-button bg-primary px-5 py-2.5 font-heading text-xs font-extrabold uppercase tracking-[0.14em] text-primary-foreground transition-transform hover:-translate-y-0.5"
          >
            Hire talent from this sector
            <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </article>
  )
}

// ─── 3D Tilt Card ───────────────────────────────────────────────────────────

function TiltCard({
  label,
  stat,
  description,
}: {
  label: string
  stat: string
  description: string
}) {
  const cardRef = useRef<HTMLDivElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const cx = (e.clientX - rect.left) / rect.width - 0.5
    const cy = (e.clientY - rect.top) / rect.height - 0.5
    card.style.transform = `perspective(800px) rotateX(${-cy * 12}deg) rotateY(${cx * 12}deg) scale(1.03)`
  }

  const handleMouseLeave = () => {
    if (!cardRef.current) return
    cardRef.current.style.transform = ""
  }

  return (
    <article
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="scroll-rise rounded-panel border border-white/12 bg-white/[0.05] p-8 backdrop-blur transition-transform duration-200 will-change-transform"
      style={{ transformStyle: "preserve-3d", transition: "transform 400ms cubic-bezier(0.2,0.8,0.2,1)" }}
    >
      <p className="font-mono text-[0.62rem] font-black uppercase tracking-[0.26em] text-primary">
        {label}
      </p>
      <p className="mt-4 font-heading text-6xl font-extrabold leading-none tracking-tight text-white">
        {stat}
      </p>
      <p className="mt-4 text-sm leading-relaxed text-white/60">{description}</p>
    </article>
  )
}

// ─── Multi-step Footer Form ────────────────────────────────────────────────

function MultiStepForm() {
  const [step, setStep] = useState<"audience" | "details">("audience")
  const [audience, setAudience] = useState<"hire" | "work" | null>(null)
  const [name, setName] = useState("")
  const [company, setCompany] = useState("")
  const [phone, setPhone] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const handleAudience = (choice: "hire" | "work") => {
    setAudience(choice)
    setStep("details")
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // In production, POST to /api/contact or equivalent
    setSubmitted(true)
  }

  return (
    <div className="rounded-panel border border-white/12 bg-white/[0.06] p-8 backdrop-blur">
      {submitted ? (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-primary/20">
            <CheckCircle2 className="size-8 text-primary" />
          </div>
          <p className="font-heading text-3xl font-extrabold text-white">
            You&apos;re on the list.
          </p>
          <p className="text-white/60">
            A Blue Core dispatcher will reach out within{" "}
            <span className="font-bold text-white">2 business hours.</span>
          </p>
        </div>
      ) : step === "audience" ? (
        <div>
          <p className="mb-6 font-mono text-[0.65rem] font-black uppercase tracking-[0.26em] text-primary">
            Get started
          </p>
          <p className="font-heading text-3xl font-extrabold leading-tight text-white">
            I am looking to...
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <button
              onClick={() => handleAudience("hire")}
              className="group flex flex-col items-start gap-3 rounded-panel border-2 border-primary/30 bg-primary/10 p-6 text-left transition-all duration-300 hover:border-primary/70 hover:bg-primary/20"
            >
              <Users className="size-7 text-primary" />
              <span className="font-heading text-xl font-extrabold text-white">
                Hire Staff
              </span>
              <span className="text-sm text-white/60">
                Need vetted warehouse labor, fast
              </span>
              <ArrowRight className="mt-2 size-4 text-primary transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => handleAudience("work")}
              className="group flex flex-col items-start gap-3 rounded-panel border-2 border-white/20 bg-white/5 p-6 text-left transition-all duration-300 hover:border-white/40 hover:bg-white/10"
            >
              <Clock4 className="size-7 text-white/70" />
              <span className="font-heading text-xl font-extrabold text-white">
                Find Work
              </span>
              <span className="text-sm text-white/60">
                Ready for a warehouse job, today
              </span>
              <ArrowRight className="mt-2 size-4 text-white/40 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <button
            type="button"
            onClick={() => { setStep("audience"); setAudience(null) }}
            className="mb-6 flex items-center gap-2 font-mono text-xs font-black uppercase tracking-[0.16em] text-white/50 hover:text-white transition-colors"
          >
            <ChevronRight className="size-3 rotate-180" />
            Back
          </button>
          <p className="font-heading text-3xl font-extrabold leading-tight text-white">
            {audience === "hire" ? "Request warehouse staff" : "Apply for warehouse work"}
          </p>
          <div className="mt-8 space-y-4">
            <div>
              <label htmlFor="name" className="mb-2 block font-mono text-[0.62rem] font-black uppercase tracking-[0.2em] text-white/60">
                Full name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="John Smith"
                className="w-full rounded-button border border-white/20 bg-white/8 px-5 py-3.5 font-sans text-sm text-white placeholder:text-white/30 focus:border-primary/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            {audience === "hire" && (
              <div>
                <label htmlFor="company" className="mb-2 block font-mono text-[0.62rem] font-black uppercase tracking-[0.2em] text-white/60">
                  Company name
                </label>
                <input
                  id="company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  placeholder="Acme Logistics"
                  className="w-full rounded-button border border-white/20 bg-white/8 px-5 py-3.5 font-sans text-sm text-white placeholder:text-white/30 focus:border-primary/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            )}
            <div>
              <label htmlFor="phone" className="mb-2 block font-mono text-[0.62rem] font-black uppercase tracking-[0.2em] text-white/60">
                Phone number
              </label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="Your best contact number"
                className="w-full rounded-button border border-white/20 bg-white/8 px-5 py-3.5 font-sans text-sm text-white placeholder:text-white/30 focus:border-primary/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <button
              type="submit"
              className="mt-2 w-full inline-flex items-center justify-center gap-3 rounded-button bg-primary py-4 font-heading text-sm font-extrabold uppercase tracking-[0.14em] text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-theme-glow"
            >
              {audience === "hire" ? "Request staff now" : "Apply for work"}
              <ArrowRight className="size-4" />
            </button>
          </div>
          <p className="mt-4 text-center font-mono text-[0.58rem] text-white/40">
            No spam. Response within 2 business hours.
          </p>
        </form>
      )}
    </div>
  )
}

// ─── Call-Off Section (Horizontal Scroll via GSAP) ────────────────────────

function CallOffSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [isMobile, setIsMobile] = useState(true)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024)
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  useEffect(() => {
    if (isMobile) return
    let ctx: { revert: () => void } | undefined

    const initGSAP = async () => {
      const { gsap } = await import("gsap")
      const { ScrollTrigger } = await import("gsap/ScrollTrigger")
      gsap.registerPlugin(ScrollTrigger)

      const section = sectionRef.current
      const track = trackRef.current
      if (!section || !track) return

      ctx = gsap.context(() => {
        const panels = track.querySelectorAll<HTMLElement>(".call-panel")
        const horizontalTween = gsap.to(track, {
          x: () => -(track.scrollWidth - window.innerWidth + 64),
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: () => `+=${track.scrollWidth - window.innerWidth + 128}`,
            scrub: 1.2,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        })
        panels.forEach((panel) => {
          gsap.fromTo(panel.querySelector(".panel-content"), { opacity: 0, y: 40 }, {
            opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
            scrollTrigger: { trigger: panel, containerAnimation: horizontalTween, start: "left 80%", toggleActions: "play none none reverse" },
          })
        })
      }, section)
    }

    const timer = setTimeout(initGSAP, 100)
    return () => { clearTimeout(timer); ctx?.revert() }
  }, [isMobile])

  const panels = (
    <>
      <div className="call-panel flex h-full w-screen shrink-0 items-center px-6 sm:px-16">
        <div className="panel-content grid w-full max-w-7xl gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <MicroLabel>The Problem</MicroLabel>
            <p className="mt-5 font-heading text-3xl font-extrabold leading-[1.1] text-white sm:text-6xl lg:text-7xl">
              Zero Idle Time. Eliminate the Cost of Warehouse Call-Offs.
            </p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
              In DFW&apos;s fast-moving logistics sector, one call-off cascades — missed shipments, overtime bills, and customer trust that takes months to rebuild.
            </p>
            <div className="mt-10 flex gap-6">
              <div className="rounded-panel border border-white/12 bg-white/[0.05] px-6 py-5 text-center backdrop-blur">
                <p className="font-heading text-3xl font-extrabold text-primary sm:text-4xl">
                  <OdometerCounter target={24} suffix="hrs" duration={1800} />
                </p>
                <p className="mt-1 font-mono text-[0.58rem] uppercase tracking-[0.2em] text-white/50">daily coverage urgency</p>
              </div>
              <div className="rounded-panel border border-white/12 bg-white/[0.05] px-6 py-5 text-center backdrop-blur">
                <p className="font-heading text-3xl font-extrabold text-primary sm:text-4xl">
                  <OdometerCounter target={30} suffix="+" duration={2000} />
                </p>
                <p className="mt-1 font-mono text-[0.58rem] uppercase tracking-[0.2em] text-white/50">years on warehouse floors</p>
              </div>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative aspect-square rounded-panel border border-white/12 bg-white/[0.04] p-8 backdrop-blur">
              <div className="absolute inset-8 rounded-full border-2 border-primary/30" aria-hidden="true" />
              <div className="absolute inset-16 rounded-full border border-primary/20" aria-hidden="true" />
              <div className="absolute inset-24 rounded-full bg-primary/10" aria-hidden="true" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Image src="/brand/call-off-visual.png" alt="Blue Core Staffing call-off visual" fill sizes="(min-width: 1024px) 400px, 300px" className="object-cover" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="call-panel flex h-full w-screen shrink-0 items-center bg-secondary px-6 sm:px-16">
        <div className="panel-content grid w-full max-w-7xl gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div className="order-2 lg:order-1 hidden lg:block">
            <div className="relative grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { step: "01", label: "Skills Assessment", desc: "In-person walk-through of equipment competency" },
                { step: "02", label: "Background Screening", desc: "7-year criminal + drug screening, MVR check" },
                { step: "03", label: "Reference Verification", desc: "Direct call to last 2 warehouse supervisors" },
                { step: "04", label: "Attendance Track", desc: "90-day attendance record pulled before any placement" },
              ].map(({ step, label, desc }) => (
                <div key={step} className="rounded-panel border border-primary/20 bg-primary/10 p-4 sm:p-5">
                  <p className="font-mono text-2xl font-black text-primary sm:text-3xl">{step}</p>
                  <p className="mt-2 font-heading text-sm font-bold text-white sm:text-base">{label}</p>
                  <p className="mt-1 text-xs text-white/50 sm:text-sm">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <MicroLabel>The Vetting Process</MicroLabel>
            <p className="mt-5 font-heading text-3xl font-extrabold leading-[1.1] text-white sm:text-6xl lg:text-7xl">
              We don&apos;t send resumes. We send warehouse-proven people.
            </p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
              Blue Core was built by warehouse operators who got tired of staffing agencies that sent warm bodies. Every candidate passes a 4-step vetting process before they ever set foot on your floor.
            </p>
            <div className="mt-8 flex items-center gap-4 rounded-panel border border-white/12 bg-white/[0.04] p-5 backdrop-blur">
              <ShieldCheck className="size-8 shrink-0 text-primary" />
              <div>
                <p className="font-heading font-bold text-white">Operator-led screening</p>
                <p className="text-sm text-white/50">Skills, attendance, and fit checked before dispatch</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="call-panel flex h-full w-screen shrink-0 items-center px-6 sm:px-16">
        <div className="panel-content w-full text-center">
          <div>
            <MicroLabel>The Result</MicroLabel>
            <p className="mt-5 font-heading text-4xl font-extrabold leading-[1.1] text-white sm:text-7xl lg:text-9xl">
              Shift-Ready{" "}<span className="text-primary">Coverage</span>
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/60 sm:text-xl">
              A cleaner operating rhythm for teams that need fewer call-off surprises, faster replacement conversations, and better-fit warehouse people.
            </p>
            <div className="mx-auto mt-12 grid max-w-3xl grid-cols-3 gap-4 sm:mt-16 sm:gap-6">
              {[
                { stat: "Fast", label: "Urgent shift response" },
                { stat: "DFW", label: "Local labor network" },
                { stat: "W-2", label: "Compliant payroll" },
              ].map(({ stat, label }) => (
                <div key={label} className="rounded-panel border border-white/12 bg-white/[0.05] p-4 backdrop-blur sm:p-6">
                  <p className="font-heading text-2xl font-extrabold text-primary sm:text-4xl">{stat}</p>
                  <p className="mt-2 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-white/50 sm:text-[0.6rem]">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )

  if (isMobile) {
    return (
      <section id="call-off" className="dark relative overflow-hidden bg-background px-6 py-16 sm:px-10">
        <h2 className="sr-only">Zero Idle Time. Eliminate the Cost of Warehouse Call-Offs.</h2>
        <div className="mx-auto max-w-[94rem] space-y-16">
          <div className="scroll-story-rise">
            <div>
              <MicroLabel>The Problem</MicroLabel>
              <p className="mt-5 font-heading text-3xl font-extrabold leading-[1.1] text-white">
                Zero Idle Time. Eliminate the Cost of Warehouse Call-Offs.
              </p>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60">
                In DFW&apos;s fast-moving logistics sector, one call-off cascades — missed shipments, overtime bills, and customer trust that takes months to rebuild.
              </p>
              <div className="mt-8 flex gap-4">
                <div className="rounded-panel border border-white/12 bg-white/[0.05] px-5 py-4 text-center backdrop-blur">
                  <p className="font-heading text-3xl font-extrabold text-primary">
                    <OdometerCounter target={24} suffix="hrs" duration={1800} />
                  </p>
                  <p className="mt-1 font-mono text-[0.58rem] uppercase tracking-[0.2em] text-white/50">daily coverage urgency</p>
                </div>
                <div className="rounded-panel border border-white/12 bg-white/[0.05] px-5 py-4 text-center backdrop-blur">
                  <p className="font-heading text-3xl font-extrabold text-primary">
                    <OdometerCounter target={30} suffix="+" duration={2000} />
                  </p>
                  <p className="mt-1 font-mono text-[0.58rem] uppercase tracking-[0.2em] text-white/50">years on warehouse floors</p>
                </div>
              </div>
            </div>
          </div>

          <div className="scroll-story-rise">
            <MicroLabel>The Vetting Process</MicroLabel>
            <p className="mt-5 font-heading text-3xl font-extrabold leading-[1.1] text-white">
              We don&apos;t send resumes. We send warehouse-proven people.
            </p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60">
              Blue Core was built by warehouse operators who got tired of staffing agencies that sent warm bodies. Every candidate passes a 4-step vetting process before they ever set foot on your floor.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { step: "01", label: "Skills Assessment", desc: "Equipment competency walk-through" },
                { step: "02", label: "Background Screening", desc: "7-year criminal + drug screening" },
                { step: "03", label: "Reference Verification", desc: "Direct call to last 2 supervisors" },
                { step: "04", label: "Attendance Track", desc: "90-day attendance record pulled" },
              ].map(({ step, label, desc }) => (
                <div key={step} className="rounded-panel border border-primary/20 bg-primary/10 p-4">
                  <p className="font-mono text-2xl font-black text-primary">{step}</p>
                  <p className="mt-2 font-heading text-sm font-bold text-white">{label}</p>
                  <p className="mt-1 text-xs text-white/50">{desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-4 rounded-panel border border-white/12 bg-white/[0.04] p-4 backdrop-blur">
              <ShieldCheck className="size-8 shrink-0 text-primary" />
              <div>
                <p className="font-heading font-bold text-white">Operator-led screening</p>
                <p className="text-sm text-white/50">Skills, attendance, and fit checked before dispatch</p>
              </div>
            </div>
          </div>

          <div className="scroll-story-rise text-center">
            <MicroLabel>The Result</MicroLabel>
            <p className="mt-5 font-heading text-4xl font-extrabold leading-[1.1] text-white">
              Shift-Ready{" "}<span className="text-primary">Coverage</span>
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/60">
              A cleaner operating rhythm for teams that need fewer call-off surprises, faster replacements, and better-fit warehouse people.
            </p>
            <div className="mx-auto mt-10 grid max-w-3xl grid-cols-3 gap-3">
              {[
                { stat: "Fast", label: "Urgent shift response" },
                { stat: "DFW", label: "Local labor network" },
                { stat: "W-2", label: "Compliant payroll" },
              ].map(({ stat, label }) => (
                <div key={label} className="rounded-panel border border-white/12 bg-white/[0.05] p-4 backdrop-blur">
                  <p className="font-heading text-2xl font-extrabold text-primary">{stat}</p>
                  <p className="mt-2 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-white/50">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="call-off" ref={sectionRef} className="dark relative overflow-hidden bg-background" style={{ height: "150vh" }}>
      <h2 className="sr-only">Zero Idle Time. Eliminate the Cost of Warehouse Call-Offs.</h2>
      <div ref={trackRef} className="flex h-screen items-center gap-0" style={{ width: "max-content" }}>
        {panels}
      </div>
    </section>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function HomePage() {
  const [activeSector, setActiveSector] = useState<string | null>(null)

  return (
    <main id="main-content" className="min-h-screen bg-background text-foreground">

      {/* ═══════════════════════════════════════════════════════════════════════
          A. HERO SECTION
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        id="hero"
        className="dark relative min-h-screen overflow-hidden bg-background"
        aria-label="Hero — Blue Core Staffing"
      >
        {/* Hero background image — behind overlays */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/brand/hero-bg.jpg"
            alt=""
            fill
            sizes="100vw"
            className="object-cover"
            priority
          />
        </div>

        {/* Ambient particle background */}
        <ParticleCanvas className="absolute inset-0 size-full z-[1]" />

        {/* Layered radial glow */}
        <div className="absolute inset-0 z-[1] bg-[radial-gradient(ellipse_70%_55%_at_50%_45%,hsl(var(--primary)/0.28),transparent_65%),radial-gradient(ellipse_50%_40%_at_80%_20%,hsl(var(--primary)/0.12),transparent_50%),linear-gradient(180deg,#1A313B_0%,hsl(var(--primary)/0.25)_30%,hsl(var(--primary)/0.25)_50%,#1A313B_80%)]" />
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(hsl(var(--foreground)/0.5)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--foreground)/0.5)_1px,transparent_1px)", backgroundSize: "64px 64px" }} aria-hidden="true" />

        {/* Floating orbit rings */}
        <div className="absolute -right-32 top-16 h-[42rem] w-[42rem] rounded-full border border-primary/15" aria-hidden="true" />
        <div className="absolute -left-20 top-40 h-[30rem] w-[30rem] rounded-full border border-primary/10" aria-hidden="true" />

        {/* ── Navigation ── */}
        <StickyHeader />

        {/* ── Hero Content ── */}
        <div className="relative z-10 mx-auto grid min-h-screen w-full max-w-[94rem] gap-10 px-6 pb-16 pt-28 sm:px-10 lg:grid-cols-[1fr_auto] lg:items-center lg:pt-32">
          {/* Left: Copy + CTAs */}
          <div className="lg:pl-[50px]">
            <h1 className="max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-7xl">
              <span className="block overflow-visible">
                <span className="block animate-[letter-reveal-up_0.8s_cubic-bezier(0.2,0.8,0.2,1)_forwards]">
                  The Backbone of
                </span>
              </span>
              <span className="block overflow-visible">
                <span className="block animate-[letter-reveal-up_0.8s_cubic-bezier(0.2,0.8,0.2,1)_0.12s_forwards] text-white">
                  DFW Logistics:
                </span>
              </span>
              <span className="block overflow-visible">
                <span className="block animate-[letter-reveal-up_0.8s_cubic-bezier(0.2,0.8,0.2,1)_0.24s_forwards]">
                  Vetted Warehouse
                </span>
              </span>
              <span className="block overflow-visible">
                <span className="block animate-[letter-reveal-up_0.8s_cubic-bezier(0.2,0.8,0.2,1)_0.36s_forwards]">
                  Staffing Solutions.
                </span>
              </span>
            </h1>

            <p className="mt-8 max-w-2xl text-lg leading-8 text-white">
              Run by warehouse operators with <strong className="text-white">30 years of direct logistics experience</strong>.
              We don&apos;t fill seats — we staff floors that run.
            </p>

            {/* Split-intent CTAs */}
            <div className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center">
              <MagneticButton href="#footer-form" variant="primary">
                FOR EMPLOYERS
                <ArrowRight className="size-4" />
              </MagneticButton>
              <MagneticButton href="#footer-form" variant="ghost">
                FOR JOB SEEKERS
                <ArrowRight className="size-4" />
              </MagneticButton>
            </div>
          </div>

        </div>

        {/* Trust indicators — full width, centered */}
        <div className="relative z-20 flex flex-wrap sm:flex-nowrap items-center justify-center gap-4 sm:gap-12 lg:gap-16 px-6 pb-32">
          {[
            { value: "30+", label: "Years ops. experience" },
            { value: "Fast", label: "Dispatch conversations" },
            { value: "DFW", label: "Local labor network" },
          ].map(({ value, label }) => (
            <div key={label} className="flex items-baseline gap-2.5">
              <p className="font-heading text-3xl font-extrabold text-primary">{value}</p>
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/50">{label}</p>
            </div>
          ))}
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="font-mono text-[0.58rem] uppercase tracking-[0.24em] text-white/40">
            Scroll
          </span>
          <div className="size-5 rounded-full border border-white/30 flex items-center justify-center">
            <ChevronDown className="size-3 text-white/40 animate-bounce" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          B. CALL-OFF REMEDY — Horizontal Scroll Section (GSAP ScrollTrigger)
      ═══════════════════════════════════════════════════════════════════════ */}
      <CallOffSection />

      {/* ═══════════════════════════════════════════════════════════════════════
          C. THE 30-YEAR OPERATIONAL EDGE — Trust & Social Proof
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        id="edge"
        className="dark relative overflow-hidden bg-background px-6 pb-28 sm:px-10 lg:pb-36 sm:-mt-96"
        aria-label="Blue Core operational experience"
      >
        {/* Background glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_30%_60%,hsl(var(--primary)/0.14),transparent_60%)]" aria-hidden="true" />

        <div className="relative z-10 mx-auto w-full max-w-[94rem]">
          {/* Section header */}
          <div className="mb-16 max-w-4xl">
            <MicroLabel>Our edge</MicroLabel>
            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-[1.1] text-white sm:text-6xl lg:text-7xl">
              We weren&apos;t born in staffing. We built our careers in warehouses.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/60">
              Most staffing agencies sell from the outside. We hire from the inside — operators who
              have worked the floor, managed the shift, and know what a reliable body is worth.
            </p>
          </div>

          {/* 3D Tilt Grid */}
          <div className="mb-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <TiltCard
              label="Operational years"
              stat="30+"
              description="Combined leadership experience from warehouse operators who moved into staffing."
            />
            <TiltCard
              label="Companies served"
              stat="DFW"
              description="Manufacturing, distribution, 3PL, and retail operations across DFW."
            />
            <TiltCard
              label="Workers placed"
              stat="Both"
              description="One funnel for employers who need coverage and job seekers who need stable work."
            />
            <TiltCard
              label="Avg. client tenure"
              stat="Ops"
              description="Messaging, vetting, and intake built around real warehouse floor pressure."
            />
          </div>

          {/* Experience breakdown */}
          <div className="mb-16 rounded-panel border border-white/12 bg-white/[0.04] p-8 backdrop-blur">
            <div className="grid gap-8 lg:grid-cols-3">
              {[
                {
                  label: "Front-line dispatch",
                  desc: "Direct intake, practical shift questions, and faster escalation when a facility needs coverage.",
                },
                {
                  label: "On-site accountability",
                  desc: "Our supervisors check in on placements during week 1. Problems are caught before they become complaints.",
                },
                {
                  label: "Payroll certainty",
                  desc: "Every worker is on our W-2. Your liability is zero. Our compliance is airtight.",
                },
              ].map(({ label, desc }) => (
                <div key={label} className="border-l-2 border-primary/60 pl-6">
                  <h3 className="font-heading text-lg font-bold text-white">{label}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          D. CORE INDUSTRIAL SECTORS HUB — Local SEO Pillar
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        id="sectors"
        className="relative overflow-hidden bg-muted/20 px-6 py-24 sm:px-10 lg:py-32 scroll-story-rise"
        aria-label="Industrial staffing sectors"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background to-muted/30" aria-hidden="true" />

        <div className="relative z-10 mx-auto w-full max-w-[94rem]">
          <div className="mb-14 max-w-3xl">
            <MicroLabel>Industries we serve</MicroLabel>
            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-[1.1] text-foreground sm:text-5xl lg:text-6xl">
              Industrial Staffing Sectors We Support Across Texas
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Industrial staffing solutions DFW teams can use for urgent coverage, seasonal peaks,
              and hard-to-fill operational roles across distribution and manufacturing floors.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:items-stretch">
            <div className="relative min-h-[34rem] overflow-hidden rounded-panel border border-border bg-card shadow-theme-card">
              <Image
                src="/brand-images/brand-image-1.png"
                alt="Blue Core Staffing industrial warehouse staffing visual"
                fill
                sizes="(min-width: 1024px) 45vw, 100vw"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/35 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="font-mono text-[0.62rem] font-black uppercase tracking-[0.22em] text-primary">
                  Local SEO pillar
                </p>
                <p className="mt-3 font-heading text-3xl font-extrabold leading-tight text-foreground">
                  Warehouse Staffing Agency Dallas-Fort Worth teams can call before the shift breaks.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {SECTORS.map((sector) => (
                <SectorAccordion
                  key={sector.id}
                  sector={sector}
                  open={activeSector === sector.id}
                  onToggle={() => setActiveSector(activeSector === sector.id ? null : sector.id)}
                />
              ))}
            </div>
          </div>

          {/* Region badges */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            {["Carrollton", "Fort Worth", "Denton", "Lewisville", "Dallas", "Plano", "Irving"].map(
              (city) => (
                <span
                  key={city}
                  className="inline-flex items-center gap-1.5 rounded-button border border-border bg-card px-4 py-2 font-mono text-xs font-semibold text-muted-foreground"
                >
                  <MapPin className="size-3 text-primary" />
                  {city}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          D2. TRUSTED BY CLIENTS — Auto-Sliding Logo Marquee
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden bg-muted/20 px-6 py-16 sm:px-10 lg:py-20 scroll-story-rise"
        aria-label="Trusted by clients"
      >
        <p className="mb-8 text-center font-mono text-[0.62rem] font-black uppercase tracking-[0.24em] text-muted-foreground">Trusted by clients</p>
        <div className="relative overflow-hidden py-4">
          <div
            className="flex gap-10"
            style={{ animation: "marquee 60s linear infinite", width: "max-content" }}
          >
            {[...Array(15)].map((_, i) => (
              <div
                key={`a-${i}`}
                className="relative h-14 w-28 shrink-0 opacity-40 grayscale transition-all duration-500 hover:opacity-90 hover:grayscale-0"
              >
                <Image
                  src={`/brand-images/client-logo-${String(i + 1).padStart(2, "0")}.png`}
                  alt={`Trusted client logo ${i + 1}`}
                  fill
                  sizes="112px"
                  className="object-contain"
                />
              </div>
            ))}
            {[...Array(15)].map((_, i) => (
              <div
                key={`b-${i}`}
                className="relative h-14 w-28 shrink-0 opacity-40 grayscale transition-all duration-500 hover:opacity-90 hover:grayscale-0"
              >
                <Image
                  src={`/brand-images/client-logo-${String(i + 1).padStart(2, "0")}.png`}
                  alt={`Trusted client logo ${i + 1}`}
                  fill
                  sizes="112px"
                  className="object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          E. FOOTER FINALE — Multi-Step Form + Magnetic Kinetic Text
      ═══════════════════════════════════════════════════════════════════════ */}
      <footer
        id="footer-form"
        className="dark relative overflow-hidden bg-background px-6 pt-20 pb-12 sm:px-10 scroll-story-rise"
        aria-label="Contact Blue Core Staffing"
      >
        {/* Top radial glow */}
        <div className="absolute inset-x-0 top-0 h-96 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,hsl(var(--primary)/0.18),transparent_60%)]" aria-hidden="true" />

        <div className="relative z-10 mx-auto w-full max-w-[94rem]">

          {/* Magnetic kinetic headline */}
          <div className="mb-20 text-center">
            <div
              className="inline-block cursor-default select-none"
              style={{ mixBlendMode: "difference" }}
              aria-hidden="true"
            >
              <p className="font-heading text-4xl font-extrabold leading-none tracking-tight text-white sm:text-7xl lg:text-9xl">
                STABILIZE YOUR
              </p>
              <p className="font-heading text-4xl font-extrabold leading-none tracking-tight text-primary sm:text-7xl lg:text-9xl">
                SUPPLY CHAIN.
              </p>
            </div>
            <p className="mt-6 font-sans text-lg text-white/60">
              Ready to stop gambling on call-offs? Let&apos;s talk.
            </p>
          </div>

          {/* Divider */}
          <div className="mb-20 border-t border-white/10" />

          {/* Two-column: form + contact info */}
          <div className="grid gap-16 lg:grid-cols-[1fr_1fr] lg:items-start">
            {/* Left: multi-step form */}
            <MultiStepForm />

            {/* Right: contact details */}
            <div className="space-y-10">
              <div>
                <p className="font-heading text-2xl font-extrabold text-white">
                  Contact Blue Core
                </p>
                <p className="mt-2 text-white/60">
                  Direct line. No automation. Human answers.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  {
                    label: "Phone",
                    value: "Contact Blue Core for dispatch",
                    href: "#footer-form",
                    note: "Mon–Fri 7am–7pm, Sat 8am–2pm",
                  },
                  {
                    label: "Email",
                    value: "Send a staffing brief",
                    href: "#footer-form",
                    note: "Response within 2 business hours",
                  },
                  {
                    label: "Location",
                    value: "DFW Metroplex",
                    href: "#sectors",
                    note: "Serving all DFW metroplex",
                  },
                ].map(({ label, value, href, note }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-full border border-white/20">
                      <MapPin className="size-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-mono text-[0.62rem] font-black uppercase tracking-[0.22em] text-primary">
                        {label}
                      </p>
                      <a href={href} className="mt-1 block font-heading text-lg font-bold text-white hover:text-primary transition-colors">
                        {value}
                      </a>
                      <p className="mt-0.5 font-sans text-sm text-white/50">{note}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-20 flex flex-col items-center justify-between gap-6 border-t border-white/10 pt-8 sm:flex-row">
            <div className="flex items-center gap-4">
              <Image
                src="/brand/logo.png"
                alt="Blue Core Staffing"
                width={96}
                height={44}
                className="object-contain opacity-60"
              />
              <p className="font-mono text-[0.62rem] font-black uppercase tracking-[0.2em] text-white/40">
                Blue Core Staffing · DFW · Est. 2016
              </p>
            </div>
            <nav aria-label="Footer navigation" className="flex flex-wrap justify-center gap-6">
              {[
                ["Privacy Policy", "#"],
                ["Terms of Service", "#"],
                ["Equal Opportunity Employer", "#"],
              ].map(([label, href]) => (
                <a
                  key={label}
                  href={href}
                  className="font-mono text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-white/40 hover:text-white/70 transition-colors"
                >
                  {label}
                </a>
              ))}
            </nav>
            <p className="font-mono text-[0.58rem] text-white/30">
              &copy; 2026 Blue Core Staffing. All rights reserved.
            </p>
          </div>
        </div>

        {/* ── LocalBusiness + Organization schema: keep fields updated as exact NAP data is finalized. ── */}
        <script
          type="application/ld+json"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": ["LocalBusiness", "Organization"],
              name: "Blue Core Staffing",
              url: "https://www.bluecore-staffing.com/",
              image: "https://www.bluecore-staffing.com/brand/hero.jpg",
              logo: "https://www.bluecore-staffing.com/brand/logo.png",
              areaServed: ["Dallas", "Fort Worth", "Carrollton", "Denton", "Lewisville", "Texas"],
              serviceType: [
                "Warehouse Staffing Agency Dallas-Fort Worth",
                "Logistics & Supply Chain Recruitment",
                "Industrial Staffing Solutions DFW",
              ],
              sameAs: [
                "https://www.facebook.com/bluecorestaffing.dallas/",
                "https://www.instagram.com/bluecorestaffing/",
              ],
            }),
          }}
        />
      </footer>
    </main>
  )
}